import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RecordsTable, type Column } from '../components/RecordsTable/RecordsTable';
import { CouponEditModal, type CouponFormValues, type Channel, type DiscountType } from '../components/CouponEditModal/CouponEditModal';
import { useMcpTools } from '../lib/useMcpTools';
import { mcpWrite } from '../lib/mcp';
import { useToast } from '../components/Toast/ToastProvider';
import { money, num } from '../lib/format';
import './Products.css'; // reuse the per-row action button styles

interface CouponRow {
  id: number;
  code: string;
  description: string | null;
  discount_type: DiscountType;
  discount_value: number | string | null; // MCP returns Postgres numeric as a STRING
  channel: Channel;
  starts_at: string | null;
  ends_at: string | null;
  status: 'active' | 'inactive';
}

const fmtDiscount = (r: CouponRow) => {
  // discount_value for `percent` is the whole percent itself (15 -> "15%"), not a fraction —
  // so it doesn't go through pct(); only the `fixed` branch is money.
  const n = num(r.discount_value);
  if (n == null) return '—';
  return r.discount_type === 'percent' ? `${n}%` : money(n);
};
const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleDateString() : '—');

/** Build tool args, omitting null optionals (the MCP tools use .optional(), not .nullable()). */
function argsOf(v: CouponFormValues): Record<string, unknown> {
  const a: Record<string, unknown> = { discountType: v.discountType, discountValue: v.discountValue, channel: v.channel };
  if (v.description != null) a.description = v.description;
  if (v.startsAt != null) a.startsAt = v.startsAt;
  if (v.endsAt != null) a.endsAt = v.endsAt;
  return a;
}

export function Config() {
  const queryClient = useQueryClient();
  const { can } = useMcpTools();
  const { notify } = useToast();
  const [editing, setEditing] = useState<CouponRow | null>(null);
  const [creating, setCreating] = useState(false);

  const canCreate = can('createCoupon');
  const canUpdate = can('updateCoupon');
  const canActivate = can('activateCoupon');
  const canDeactivate = can('deactivateCoupon');
  // C2: the channel-sync capability does not exist in cabana-mcp yet (coupons are DB-only CRUD,
  // no push leg), so this is always false and the Sync button below stays disabled.

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['mcp', 'listCoupons'] });

  const createCoupon = useMutation({
    mutationFn: (v: CouponFormValues) => mcpWrite('createCoupon', { code: v.code, ...argsOf(v) }),
    onSuccess: (_d, v) => {
      void refresh();
      setCreating(false);
      notify({ message: `Coupon ${v.code} created.`, variant: 'success' });
    },
    onError: (err: Error) => notify({ message: err.message, variant: 'error' }),
  });

  const updateCoupon = useMutation({
    mutationFn: (vars: { id: number; v: CouponFormValues }) => mcpWrite('updateCoupon', { id: vars.id, ...argsOf(vars.v) }),
    onSuccess: () => {
      void refresh();
      setEditing(null);
      notify({ message: 'Coupon updated.', variant: 'success' });
    },
    onError: (err: Error) => notify({ message: err.message, variant: 'error' }),
  });

  const setStatus = useMutation({
    mutationFn: (vars: { id: number; active: boolean }) =>
      mcpWrite(vars.active ? 'activateCoupon' : 'deactivateCoupon', { id: vars.id }),
    onSuccess: (_d, vars) => {
      void refresh();
      notify({ message: `Coupon ${vars.active ? 'activated' : 'deactivated'}.`, variant: 'success' });
    },
    onError: (err: Error) => notify({ message: err.message, variant: 'error' }),
  });

  const columns: Column<CouponRow>[] = [
    { key: 'code', header: 'Code' },
    { key: 'channel', header: 'Channel', render: (r) => <span className="records__tag">{r.channel}</span> },
    { key: 'discount', header: 'Discount', numeric: true, render: fmtDiscount },
    { key: 'starts_at', header: 'Starts', render: (r) => fmtDate(r.starts_at) },
    { key: 'ends_at', header: 'Ends', render: (r) => fmtDate(r.ends_at) },
    { key: 'description', header: 'Description', render: (r) => r.description || '—' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <span className={`records__tag records__tag--${r.status === 'active' ? 'ok' : 'warn'}`}>{r.status}</span>
      ),
    },
  ];

  const actions =
    canUpdate || canActivate || canDeactivate
      ? {
          header: 'Actions',
          render: (row: CouponRow) => (
            <span className="products__row-actions">
              {canUpdate && (
                <button type="button" className="products__row-btn" onClick={() => setEditing(row)}>
                  Edit
                </button>
              )}
              {row.status === 'active'
                ? canDeactivate && (
                    <button
                      type="button"
                      className="products__row-btn"
                      disabled={setStatus.isPending}
                      onClick={() => setStatus.mutate({ id: row.id, active: false })}
                    >
                      Deactivate
                    </button>
                  )
                : canActivate && (
                    <button
                      type="button"
                      className="products__row-btn"
                      disabled={setStatus.isPending}
                      onClick={() => setStatus.mutate({ id: row.id, active: true })}
                    >
                      Activate
                    </button>
                  )}
            </span>
          ),
        }
      : undefined;

  const toolbar = (
    <>
      {canCreate && (
        <button type="button" className="records__retry" onClick={() => setCreating(true)}>
          New coupon
        </button>
      )}
    </>
  );

  return (
    <>
      <RecordsTable<CouponRow>
        title="Config — Coupons"
        intro="Promotional discount codes. Create, edit, and activate or deactivate coupons here — this is the audited system of record; the consumer checkout redeems them directly."
        tool="listCoupons"
        args={{ limit: 200 }}
        select={(r) => (r as { coupons: CouponRow[] }).coupons ?? []}
        columns={columns}
        rowKey={(r) => r.id}
        emptyMessage="No coupons yet."
        toolbar={toolbar}
        actions={actions}
        refreshable
        rowClassName={(r) => (r.status !== 'active' ? 'records__row--inactive' : undefined)}
      />

      {creating && (
        <CouponEditModal
          mode="create"
          saving={createCoupon.isPending}
          onClose={() => setCreating(false)}
          onSave={(v) => createCoupon.mutate(v)}
        />
      )}

      {editing && (
        <CouponEditModal
          mode="edit"
          initial={editing}
          saving={updateCoupon.isPending}
          onClose={() => setEditing(null)}
          onSave={(v) => updateCoupon.mutate({ id: editing.id, v })}
        />
      )}
    </>
  );
}
